/**
 * InlineError — small actionable error surface for mutation failures.
 *
 * The full-screen `ErrorBoundary` only catches render-time throws.
 * Most failures an athlete sees in practice are mutation rejections
 * (PATCH/POST returned 4xx/5xx, network blip). Those don't crash
 * the tree; we still need to tell the user "this didn't go through"
 * and offer a retry without forcing a page reload.
 *
 * Slots: optional title (defaults via i18n), required message, optional
 * onRetry handler. When `onRetry` is provided the retry button appears.
 *
 * Visually: a quiet rose-tinted card, WarningCircle icon, message body,
 * retry button on the right. role="alert" + aria-live so screen
 * readers announce failures immediately.
 */
import type { ReactNode } from "react";
import { WarningCircle, ArrowClockwise } from "@phosphor-icons/react";


type Props = {
  /** Headline — usually a short "Что-то пошло не так" / "Action failed". */
  title?: string;
  /** What went wrong. Either a string (we'll wrap it) or pre-styled
   *  ReactNode for richer error displays. */
  message: ReactNode;
  /** When set, renders a retry button calling this handler. */
  onRetry?: () => void;
  /** Label of the retry button. Caller controls i18n. */
  retryLabel?: string;
  /** Pin in tests. */
  testId?: string;
  className?: string;
};


export function InlineError({
  title,
  message,
  onRetry,
  retryLabel = "Retry",
  testId,
  className = "",
}: Props) {
  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid={testId ?? "inline-error"}
      className={`rounded-card border border-rose-500/30 bg-rose-500/5 p-3 flex items-start gap-3 ${className}`}
    >
      <WarningCircle
        className="w-5 h-5 text-rose-300 shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        {title && (
          <div className="text-sm font-semibold text-fg-primary">{title}</div>
        )}
        <div className="text-xs text-fg-secondary leading-relaxed">
          {message}
        </div>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          data-testid={testId ? `${testId}-retry` : "inline-error-retry"}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-pill ring-1 ring-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 text-[11px] font-medium"
        >
          <ArrowClockwise className="w-3 h-3" aria-hidden="true" />
          {retryLabel}
        </button>
      )}
    </div>
  );
}
